import { SharedSpinner } from '../../Loading/SharedSpinner';
import { BLUE } from '../../Theme/palette';
import { AppstoreOutlined, CheckOutlined, CloseOutlined, SearchOutlined } from '@ant-design/icons';
import { DrawerCloseButton } from '@components';
import { useSelectServiceGroups, useView } from '@hooks';
import { L } from '@lib';
import { ServiceGroup } from '@services';
import { Button, Drawer, Empty, Input, Space, Tag, Typography } from 'antd';
import React, { useEffect, useMemo, useRef } from 'react';

const { Text } = Typography;

interface IProps {
  open: boolean;
  mode: 'single' | 'multiple';
  selectedIds: number[];
  selectedLabels?: Record<number, string>;
  title?: string;
  onClose: () => void;
  onSelect: (serviceGroup: ServiceGroup) => void;
  onDeselect: (id: number) => void;
  onClear?: () => void;
}

export const ServiceGroupPickerDrawer: React.FC<IProps> = ({
  open,
  mode,
  selectedIds,
  selectedLabels,
  title,
  onClose,
  onSelect,
  onDeselect,
  onClear,
}) => {
  const { isMobile } = useView();
  const { serviceGroups, loading, hasMore, handleSearch, loadMore } = useSelectServiceGroups({
    enabled: open,
  });

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<any>(null);

  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => searchInputRef.current?.focus?.(), 150);
    return () => window.clearTimeout(id);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!sentinelRef.current || !listRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !loading) {
          loadMore();
        }
      },
      { root: listRef.current, rootMargin: '120px', threshold: 0 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [open, hasMore, loading, loadMore]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const handleItemClick = (group: ServiceGroup) => {
    if (mode === 'single') {
      onSelect(group);
      onClose();
      return;
    }
    if (selectedSet.has(group.id)) {
      onDeselect(group.id);
    } else {
      onSelect(group);
    }
  };

  const renderSelectedChips = () => {
    if (mode !== 'multiple' || selectedIds.length === 0) return null;
    return (
      <div
        style={{
          padding: '8px 16px',
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 4,
          alignItems: 'center',
        }}
      >
        {selectedIds.map((id) => {
          const label = selectedLabels?.[id] ?? `#${id}`;
          return (
            <Tag
              key={id}
              icon={<AppstoreOutlined />}
              closable
              onClose={(e) => {
                e.preventDefault();
                onDeselect(id);
              }}
              style={{
                margin: 0,
                padding: '2px 8px',
                background: '#f5f5f5',
                border: '1px solid #bfbfbf',
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 500,
                color: '#333',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              {label}
            </Tag>
          );
        })}
        {onClear && (
          <Button type="link" size="small" onClick={onClear} style={{ marginLeft: 'auto' }}>
            {L('Clear all')}
          </Button>
        )}
      </div>
    );
  };

  const renderListItem = (group: ServiceGroup) => {
    const isSelected = selectedSet.has(group.id);
    return (
      <div
        key={group.id}
        onClick={() => handleItemClick(group)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 16px',
          cursor: 'pointer',
          background: isSelected ? BLUE.soft : 'transparent',
          borderBottom: '1px solid #f5f5f5',
          transition: 'background 0.15s',
        }}
        onMouseEnter={(e) => {
          if (!isSelected) (e.currentTarget as HTMLDivElement).style.background = '#fafafa';
        }}
        onMouseLeave={(e) => {
          if (!isSelected) (e.currentTarget as HTMLDivElement).style.background = 'transparent';
        }}
      >
        <AppstoreOutlined style={{ color: isSelected ? BLUE.badge : '#8c8c8c', fontSize: 16 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text style={{ fontWeight: 500, color: '#333' }} ellipsis>
            {group.name}
          </Text>
          {group.description && (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
                {group.description}
              </Text>
            </div>
          )}
        </div>
        {isSelected && <CheckOutlined style={{ color: BLUE.badge }} />}
      </div>
    );
  };

  return (
    <Drawer
      title={title ?? L('Service category select')}
      open={open}
      onClose={onClose}
      placement="right"
      width={isMobile ? '100%' : 480}
      destroyOnHidden
      closable={false}
      extra={<DrawerCloseButton icon={<CloseOutlined />} onClick={onClose} />}
      styles={{ body: { padding: 0, display: 'flex', flexDirection: 'column' } }}
      footer={
        mode === 'multiple' ? (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {selectedIds.length} {L('selected')}
            </Text>
            <Space>
              <Button onClick={onClose}>{L('Cancel')}</Button>
              <Button type="primary" onClick={onClose}>
                {L('Done')}
              </Button>
            </Space>
          </div>
        ) : null
      }
    >
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
        <Input
          ref={searchInputRef}
          allowClear
          prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
          placeholder={L('Search categories')}
          onChange={(e) => handleSearch(e.target.value)}
        />
      </div>

      {renderSelectedChips()}

      <div ref={listRef} style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {serviceGroups.map(renderListItem)}

        {serviceGroups.length === 0 && !loading && (
          <div style={{ padding: 32 }}>
            <Empty description={L('No categories found')} />
          </div>
        )}

        {loading && (
          <div style={{ textAlign: 'center', padding: 12 }}>
            <SharedSpinner size="small" loading />
          </div>
        )}

        <div ref={sentinelRef} style={{ height: 1 }} />
      </div>
    </Drawer>
  );
};
